import { useCallback, useEffect, useState } from 'react'
import { Button, Descriptions, Divider, Modal, Select, Space, Table, TableProps } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import CustomTable from '../../components/table'
import defaultParams from '../../assets/constants/default_params'
import { toastError, toastSuccess } from '../../utils/toast'
import { useLoading } from '../../context/LoadingContext'
import { apiGetOrderTransactionConnectionDetail } from '../../api'
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import ButtonBack from '../../components/button/ButtonBack'
import { useSearchParams } from 'react-router-dom'
import { renderDateByUnix, renderStatus, renderType } from '../kit-sim/method'

export default function Detail() {
    const [transactionConnection, setTransactionConnection] = useState<any>()
    const [listTran, setListTran] = useState<any>([])
    const [listTranDescOption, setListTranDescOption] = useState<any>([])
    const [orderDetail, setOrderDetail] = useState<any>({})
    const [listExport, setListExport] = useState<any>([])
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [searchExport, setSearchExport] = useState({
        status: '',
        description: 'ALL',
    })
    const [isOpenModalDownload, setIsOpenModalDownload] = useState<boolean>(false)
    const { setIsLoading } = useLoading()
    const [searchParams] = useSearchParams()
    const orderId = searchParams.get('orderId')

    const columns: TableProps<any>['columns'] = [
        {
            key: 'phoneSim',
            title: 'Msisdn',
            dataIndex: 'phoneSim',
        },
        {
            key: 'serialSim',
            title: 'Serial Sim',
            dataIndex: 'serialSim',
        },
        {
            key: 'startDate',
            title: 'Ngày đấu nối',
            dataIndex: 'startDate',
            render: startDate => renderDateByUnix(startDate),
        },
        {
            key: 'dataPack',
            title: 'Gói cước',
            dataIndex: 'dataPack',
        },
        {
            key: 'type',
            title: 'Loại gói cước',
            dataIndex: 'type',
            render: type => renderType(type),
        },
        {
            key: 'status',
            title: 'Trạng thái',
            dataIndex: 'status',
            render: status => renderStatus(status),
        },
        {
            key: 'description',
            title: 'Mô tả',
            dataIndex: 'description',
        },
    ]

    const exportColumns: TableProps<any>['columns'] = [
        {
            key: 'phoneSim',
            title: 'Msisdn',
            dataIndex: 'phoneSim',
        },
        {
            key: 'serialSim',
            title: 'Serial Sim',
            dataIndex: 'serialSim',
        },
        {
            key: 'status',
            title: 'Trạng thái',
            dataIndex: 'status',
        },
        {
            key: 'description',
            title: 'Mô tả',
            dataIndex: 'description',
        },
    ]

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true)
            const resp = await apiGetOrderTransactionConnectionDetail({
                orderId: orderId,
                page: currentPage,
                size: pageSize,
            })
            setTransactionConnection(resp.data)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, currentPage, pageSize, orderId])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const fetchFullOrder = useCallback(async () => {
        try {
            setIsLoading(true)
            const resp = await apiGetOrderTransactionConnectionDetail({
                orderId: orderId,
                page: defaultParams.PAGE,
                size: defaultParams.MAX_LIMIT,
            })
            setListTran(resp.data?.transactions)
            let listTranDesc = [] as any
            resp.data.transactions?.map((item: any) => {
                if (!listTranDesc.includes(item.description)) {
                    listTranDesc.push(item.description)
                }
                return item
            })
            setListTranDescOption(listTranDesc.map((item: any) => ({ label: item || '(Trống)', value: item })))
            setOrderDetail({
                totalSim: resp.data?.total,
                totalSuccess: resp.data?.transactions.reduce((acc: any, item: any) => item.status === 'SUCCESS' ? acc + 1 : acc, 0),
                totalFail: resp.data?.transactions.reduce((acc: any, item: any) => item.status === 'FAIL' ? acc + 1 : acc, 0),
            })
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [orderId])

    useEffect(() => {
        fetchFullOrder()
    }, [fetchFullOrder])

    useEffect(() => {
        if (listTran.length === 0) return
        let dataExport = listTran
        if (searchExport.description !== 'ALL') {
            dataExport = dataExport.filter((item: any) => item.description === searchExport.description)
        }
        if (searchExport.status) {
            dataExport = dataExport.filter((item: any) => item.status === searchExport.status)
        }
        setListExport(dataExport)
    }, [searchExport, listTran])

    const handleDownloadExcel = async () => {
        try {
            setIsLoading(true)
            const dataExcel = listExport?.map((item: any) => ({
                'Msisdn': item.phoneSim,
                'Serial Sim': item.serialSim,
                'Trạng thái': item.status,
                'Mô tả': item.description,
            }))
            const ws = XLSX.utils.json_to_sheet(dataExcel)
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, "Sheet1")
            const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" })
            const blob = new Blob([excelBuffer], { type: "application/octet-stream" })
            saveAs(blob, `${orderId}${searchExport.status && `-${searchExport.status}`}${searchExport.description && `-${searchExport.description}`}.xlsx`)
            setIsOpenModalDownload(false)
            toastSuccess('Đã tải xuống!')
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <ButtonBack />
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 20 }}>
                <h2>Mã đơn hàng: {orderId}</h2>
                <Button onClick={() => setIsOpenModalDownload(true)} icon={<DownloadOutlined />}>Tải xuống (File Excel)</Button>
            </div>
            <Descriptions
                style={{ maxWidth: 600 }}
                items={[
                    {
                        key: 'total',
                        label: 'Tổng SIM',
                        children: orderDetail.totalSim,
                    },
                    {
                        key: 'totalSuccess',
                        label: 'Tổng thành công',
                        children: orderDetail.totalSuccess,
                    },
                    {
                        key: 'totalFail',
                        label: 'Tổng thất bại',
                        children: orderDetail.totalFail,
                    },
                ]}
            />
            <CustomTable
                namePage='giao dịch'
                columns={columns}
                dataSource={transactionConnection?.transactions || []}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={transactionConnection?.total}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
            <Modal
                className='modal-export'
                title='Tải xuống (File Excel)'
                open={isOpenModalDownload}
                footer={[]}
                onCancel={() => setIsOpenModalDownload(false)}>
                <Divider style={{ marginTop: 10, marginBottom: 10 }} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginBottom: 10 }}>
                    <Space>
                        <span>Trạng thái:</span>
                        <Select
                            onChange={value => setSearchExport({ ...searchExport, status: value })}
                            style={{ minWidth: 170 }}
                            defaultValue={''}
                            options={[
                                { label: 'Tất cả', value: '' },
                                { label: 'Thành công', value: 'SUCCESS' },
                                { label: 'Thất bại', value: 'FAIL' },
                                { label: 'Đã tạo', value: 'CREATED' },
                            ]}
                        />
                    </Space>
                    <Space>
                        <span>Mô tả:</span>
                        <Select
                            onChange={value => setSearchExport({ ...searchExport, description: value })}
                            style={{ minWidth: 170 }}
                            defaultValue={'ALL'}
                            options={[
                                { label: 'Tất cả', value: 'ALL' },
                                ...listTranDescOption,
                            ]}
                        />
                    </Space>
                </div>
                <Table
                    size='small'
                    rowKey={(_, index: any) => index}
                    style={{ overflow: 'auto', marginBottom: 20 }}
                    columns={exportColumns}
                    dataSource={listExport}
                    pagination={{ pageSize: 10 }}
                />
                <div style={{ textAlign: 'right' }}>
                    <Button disabled={listExport?.length === 0} onClick={handleDownloadExcel} type='primary' icon={<DownloadOutlined />}>
                        Tải xuống ({listExport?.length})
                    </Button>
                </div>
            </Modal>
        </div>
    )
}
